import {
  AxisConfig,
  ChartsXAxisProps,
  ChartsYAxisProps,
  ScaleName,
} from '@mui/x-charts';
import { GlobalDisplayState } from '../../../../../../context/types';
import { SortTypes } from '../../../../../types';

export type XAxisConfig<T> = AxisConfig<ScaleName, T, ChartsXAxisProps> & {
  height?: number;
  transform?: {
    x: number;
    y: number;
    rotation: number;
  };
};

export type YAxisConfig<T> = Omit<
  AxisConfig<ScaleName, T, ChartsYAxisProps>,
  'position' | 'dataKey'
> & {
  position: Required<ChartsYAxisProps>['position'];
};

export enum AxisConfigType {
  SINGLE = 'single',
  DOUBLE = 'double',
}

export type SingleAxisConfig<
  AxisConfig extends XAxisConfig<any> | YAxisConfig<any>,
> = {
  type: AxisConfigType.SINGLE;
  firstAxisConfig: AxisConfig;
};

type DoubleAxisConfig<AxisConfig extends XAxisConfig<any> | YAxisConfig<any>> =
  {
    type: AxisConfigType.DOUBLE;
    // using convention of first to mean left for vertical axis and bottom for horizontal axis
    // and second to mean right for vertical axis and top for horizontal axis
    firstAxisConfig: AxisConfig;
    secondAxisConfig: AxisConfig;
  };

export type SingleOrDoubleAxisConfig<
  AxisConfig extends XAxisConfig<any> | YAxisConfig<any>,
> = SingleAxisConfig<AxisConfig> | DoubleAxisConfig<AxisConfig>;
export interface TitleConfig {
  mainTitle: string;
  extraContent?: React.ReactNode;
}

// TODO: This is not really a common chart type, but it's used in the bar chart
// But keeping it here simplifies things significantly
// as then ChartDisplayStateConfig and ChartDisplayStateUpdateOptions can be shared
// Later on, I might think of a better way to do this
export enum BarChartLayouts {
  STACKED = 'stacked',
  GROUPED = 'grouped',
  PERCENTAGE = 'percentage',
}

export interface AllChartDisplayStateConfig {
  enableLabels: boolean;
  showLabels: boolean;
  enablePercentage: boolean;
  enableStack: boolean;
  layout: BarChartLayouts;
  enableLegend: boolean;
  showLegend: boolean;
  enableSortOrder: boolean;
  selectedSortOrder: SortTypes;
  enableTableView: boolean;
  showTableView: boolean;
  showGridLines: boolean;
  enableExport: boolean;
  enableWrappedXAxisLabels: boolean;
  showWrappedXAxisLabels: boolean;
}

export type ChartDisplayStateConfig = Partial<AllChartDisplayStateConfig>;

export interface AllChartDisplayStateUpdateOptions {
  onClickLabels: () => void;
  onClickPercentage: () => void;
  onClickStack: () => void;
  onClickLegend: () => void;
  onSelectSortOrder: (selectedSortOrder: SortTypes) => void;
  onClickTableView: () => void;
  onClickFullScreen: () => void;
  onClickWrappedXAxisLabels: () => void;
}

export type ChartDisplayStateUpdateOptions =
  Partial<AllChartDisplayStateUpdateOptions>;

export type ChartDisplayConfig = ChartDisplayStateConfig &
  ChartDisplayStateUpdateOptions;

export interface DataMapValue {
  datasetKey: string;
  value: number;
}

export type SortedLabels = {
  [SortTypes.ASC]: string[];
  [SortTypes.DESC]: string[];
};

export type ChartDataMap = Record<string, DataMapValue[]>;

export type Dataset = {
  value: string;
  label?: string;
  valueFormatter?: (value: number) => string;
};

// TODO: Maybe these should be in bar chart types as these
// seem to be bar chart specific
interface CommonChartDataFields {
  labels: string[];
  sortedLabels?: SortedLabels;
  dataMap: ChartDataMap;
}
export interface SingleDatasetChartData extends CommonChartDataFields {
  type: 'single-dataset';
  datasets: [Dataset];
}

interface BaseMultiDatasetChartData extends CommonChartDataFields {
  type: 'multi-dataset';
  datasets: Dataset[];
}

/**
 * Stackable multi-dataset chart data where segments can be stacked on top of each other
 * (e.g., age groups within each year). This means:
 * - The chart can display stacked/percentage layouts
 * - A "Total" column can be shown in the table view if `showTotal` is true
 *   (total is computed by summing all segment values for each label)
 */
interface StackableMultiDatasetChartData extends BaseMultiDatasetChartData {
  isStackable: true;
  /** When true, shows a "Total" column in table view by summing all segment values */
  showTotal?: boolean;
}

/**
 * Non-stackable multi-dataset chart data where datasets are independent series
 * that shouldn't be summed (e.g., different metrics sharing the same x-axis).
 */
interface NonStackableMultiDatasetChartData extends BaseMultiDatasetChartData {
  isStackable: false;
}

/**
 * Chart data with multiple datasets (e.g., segmented data with multiple categories).
 * Use `isStackable: true` when segments can be stacked and totaled.
 * Use `isStackable: false` when datasets are independent series.
 */
export type MultiDatasetChartData =
  | StackableMultiDatasetChartData
  | NonStackableMultiDatasetChartData;

export type ChartData = SingleDatasetChartData | MultiDatasetChartData;

export interface ProcessTableDataOptions {
  layout: BarChartLayouts;
  selectedSortOrder?: SortTypes | null;
}

export type TogglePercentageAction = {
  type: 'toggle-percentage';
};

export type ToggleTableViewAction = {
  type: 'toggle-table-view';
};

export type ToggleStackAction = {
  type: 'toggle-stack';
};

export type ToggleLabelsAction = {
  type: 'toggle-labels';
};

export type ToggleLegendAction = {
  type: 'toggle-legend';
};

export type ToggleWrappedXAxisLabelsAction = {
  type: 'toggle-wrapped-x-axis-labels';
};

export type ToggleSortOrderAction = {
  type: 'toggle-sort-order';
  selectedSortOrder: SortTypes;
};

export type GlobalStateSyncAction = {
  type: 'global-state-sync';
  globalDisplayState: GlobalDisplayState;
};

export type ChartDisplayAction =
  | TogglePercentageAction
  | ToggleTableViewAction
  | ToggleStackAction
  | ToggleLabelsAction
  | ToggleLegendAction
  | ToggleWrappedXAxisLabelsAction
  | ToggleSortOrderAction
  | GlobalStateSyncAction;

export type ChartDisplayStateAndGlobalDisplayState = ChartDisplayStateConfig & {
  globalDisplayState: GlobalDisplayState;
};
